/**
 * Shared error state component for consistent UI.
 * Canonical location: blocks/error. Re-exported from ui/error for backward compatibility.
 */

import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

export interface ErrorStateProps {
  /** Short title (e.g. "Something went wrong"). */
  title?: string;
  /** User-facing description (required). Aliased from message for backward compat. */
  description?: string;
  /** @deprecated Use description. Shown as main message when description not provided. */
  message?: string;
  /** Optional raw error message or stack for collapsible details. Aliased from details. */
  error?: string | null;
  /** @deprecated Use error for collapsible technical details. */
  details?: string | null;
  /** Retry callback; when provided, shows "Retry" button. */
  onRetry?: () => void;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  description: descriptionProp,
  message,
  error: errorProp,
  details,
  onRetry,
  className,
}) => {
  const description = descriptionProp ?? message ?? '';
  const error = errorProp ?? details ?? null;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasDetails = !!error?.trim();

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
      role="alert"
    >
      <div className="max-w-md">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-destructive/20 mb-4">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold mb-2 text-foreground">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {description}
        </p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mb-4">
            Retry
          </Button>
        )}
        {hasDetails && (
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <CollapsibleTrigger
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {detailsOpen ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show details
                </>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 text-left text-xs text-muted-foreground bg-muted p-3 rounded overflow-auto max-h-32">
                {error}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
};
